import { Typography } from '@mui/material';
import type { ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';

/**
 * The card chrome every settings and detail section is built from.
 *
 * Header tone is deliberately NOT decoration: neutral is the default, and a
 * colour is passed only when it carries a verdict the director needs to read.
 * That is what keeps amber and red meaningful instead of ambient.
 */
export type DetailCardTone = 'neutral' | 'success' | 'warning' | 'danger';

const TONE_CLASSES: Record<DetailCardTone, { header: string; tile: string }> = {
  neutral: {
    // The same panel gradient the hero carries, so a section header and the
    // hero read as one surface family. It runs to its darkest stop on the
    // right, where the header meets the card's own edge.
    header: 'bg-linear-to-r from-panel-from via-panel-via to-panel-to',
    // White, not a tint: the tile sits ON the gradient, and a low rung of the
    // same ramp lands between the gradient's own stops and disappears.
    tile: 'bg-surface text-primary-dark',
  },
  success: {
    header: 'bg-linear-to-r from-success/8 to-surface',
    tile: 'bg-success/12 text-success',
  },
  warning: {
    header: 'bg-linear-to-r from-warning/8 to-surface',
    tile: 'bg-warning/12 text-warning',
  },
  danger: {
    header: 'bg-linear-to-r from-error/8 to-surface',
    tile: 'bg-error/12 text-error',
  },
};

interface DetailCardProps {
  children: ReactNode;
  className?: string;
}

export const DetailCard = ({ children, className }: DetailCardProps) => (
  <section
    className={cn(
      'overflow-hidden rounded-xl border border-divider bg-surface shadow-card',
      className,
    )}
  >
    {children}
  </section>
);

interface DetailCardHeaderProps {
  /** Sized at 20px by the tile, to match its siblings. */
  icon?: ReactNode;
  title: string;
  subtitle?: string;
  /** Right-aligned slot — a status chip, or a button that acts on the card. */
  action?: ReactNode;
  tone?: DetailCardTone;
}

/**
 * On a phone the action wraps to its own full-width row beneath the title
 * rather than being squeezed alongside it, which is what keeps an "Edit"
 * button at a tappable width at 360px.
 */
export const DetailCardHeader = ({
  icon,
  title,
  subtitle,
  action,
  tone = 'neutral',
}: DetailCardHeaderProps) => {
  const classes = TONE_CLASSES[tone];

  return (
    <div
      className={cn(
        'flex flex-col gap-2.5 border-b border-divider px-4 py-3',
        'sm:flex-row sm:items-center sm:justify-between sm:px-5',
        classes.header,
      )}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        {icon !== undefined && (
          <span
            className={cn(
              'flex size-8 shrink-0 items-center justify-center rounded-lg',
              '[&>svg]:text-[18px]',
              classes.tile,
            )}
          >
            {icon}
          </span>
        )}
        <div className="min-w-0">
          <Typography
            variant="h2"
            component="h2"
            className="truncate text-[15px] leading-tight font-bold"
          >
            {title}
          </Typography>
          {subtitle !== undefined && (
            <Typography variant="caption" color="text.secondary" component="p">
              {subtitle}
            </Typography>
          )}
        </div>
      </div>

      {action !== undefined && <div className="shrink-0">{action}</div>}
    </div>
  );
};

interface DetailCardBodyProps {
  children: ReactNode;
  className?: string;
}

export const DetailCardBody = ({ children, className }: DetailCardBodyProps) => (
  <div className={cn('p-4 sm:p-5', className)}>{children}</div>
);

interface DetailCardSectionProps {
  icon?: ReactNode;
  title: string;
  subtitle?: string;
  action?: ReactNode;
  tone?: DetailCardTone;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}

/**
 * A card with its header already wired — the shape every list screen uses.
 *
 * The list screens were assembling a filter bar, a table and a pager as three
 * bare siblings on the page background while the settings cards had a header,
 * a border and a body around theirs. Same content, two different products.
 * Composing the three parts here is what stops that drifting again: a list is
 * a section of the page, not loose furniture on it.
 */
export const DetailCardSection = ({
  icon,
  title,
  subtitle,
  action,
  tone,
  children,
  className,
  bodyClassName,
}: DetailCardSectionProps) => (
  <DetailCard className={className}>
    <DetailCardHeader
      icon={icon}
      title={title}
      subtitle={subtitle}
      action={action}
      tone={tone}
    />
    <DetailCardBody className={cn('flex flex-col gap-4', bodyClassName)}>
      {children}
    </DetailCardBody>
  </DetailCard>
);
